<script>
import axios from 'axios';
import Vue from 'vue';
import { mapGetters } from 'vuex';

import Multiselect from 'vue-multiselect';

const indexForThisTab = 5;

export default {
  name: 'TabMarketing',
  props: {
    tab: Object,
    client: Object,
    editing: Boolean,
    currentTab: { type: Number, required: true },
    clientTemplateId: { type: Number, required: false },
  },
  components: {
    Multiselect,
  },
  data: () => ({
    aso_groups: {
      loaded: false,
      loading: false,
      data: [],
      selected: null,
    },
    marketing_plans: {
      loaded: false,
      loading: false,
      data: [],
      active_marketing_plan: null,
      tableFields: [
        { key: 'uuid', label: '#' },
        { key: 'key', label: 'Key' },
        { key: 'aso_group', label: 'ASO Group' },
        { key: 'start_date', label: 'Start date' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
    },
    incentivesOptInOptions: [
      { text: 'Pre-enrollment', value: 'pre_enrollment' },
      { text: 'Post-enrollment', value: 'post_enrollment' },
    ],
  }),
  computed: {
    ...mapGetters({
      isMarketingIntegrationEnabled: 'Core/isMarketingIntegrationEnabled',
    }),
    marketingPlansOverlay() {
      if (this.marketing_plans.loading) {
        return {
          show: true,
          icon: 'arrow-clockwise',
          animation: 'spin',
          text: 'Fetching marketing plan',
        };
      }
      return {
        show: false,
        icon: '',
        animation: '',
        text: '',
      };
    },
    tabIcon() {
      if (this.$v.$invalid) {
        return 'alert-triangle';
      }
      return 'check';
    },
    asoGroupOptions() {
      return this.aso_groups.data.map(group => group.aso_group);
    },
  },
  watch: {
    $v: {
      handler(value) {
        this.tab.isValid = !value.$invalid;
      },
      deep: true,
      immediate: true,
    },
    currentTab(value) {
      if (!this.isMarketingIntegrationEnabled) return;

      if (indexForThisTab === value && this.marketing_plans.loaded === false) {
        this.fetchMarketingPlan();
        this.fetchASOGroups();
      }
    },
  },
  validations() {
    return {};
  },
  methods: {
    fetchMarketingPlan() {
      if (this.clientTemplateId || !this.client?.id) {
        return Promise.resolve();
      }
      this.marketing_plans.loading = true;
      return axios.get('v1/marketing/marketing-plan', { params: { client_id: this.client.id } })
        .then(response => {
          if (!response.data.marketing_plans || response.data.marketing_plans.length < 1) {
            return;
          }
          this.marketing_plans.data = response.data.marketing_plans;
          this.marketing_plans.active_marketing_plan = response.data.marketing_plans.find(plan => plan.status === 'active');
        }).catch(e => {
          Vue.prototype.$noty.error('Cannot fetch marketing plan', e);
        }).finally(() => {
          this.marketing_plans.loading = false;
          this.marketing_plans.loaded = true;
        });
    },
    fetchASOGroups() {
      if (!this.client?.id) {
        return Promise.resolve();
      }
      this.aso_groups.loading = true;
      return axios.get(`v1/clients/${this.client.id}/multi-tenants/all`)
        .then(response => {
          this.aso_groups.data = response.data.data;
        }).catch(e => {
          Vue.prototype.$noty.error('Cannot fetch ASO groups', e);
        }).finally(() => {
          this.aso_groups.loading = false;
          this.aso_groups.loaded = true;
        });
    },
  },
};
</script>

<template>
  <b-tab title-item-class="mt-1">
    <template #title>Marketing <feather :type="tabIcon" class="float-right"/></template>
    <b-card-text>
      <b-tabs content-class="mt-3">
        <b-tab active title="General">
          <b-row>
            <b-col xl="4">
              <b-row>
                <b-col cols="12">
                  <label>
                    <toggle-button id="checkbox-client-toggles.send_drop_off_emails"
                                   v-model="client.configuration.toggles.send_drop_off_emails"
                                   name="checkbox-client-toggles.send_drop_off_emails"
                                   sync />
                    Send drop-off emails
                  </label>
                </b-col>
                <b-col cols="12">
                  <label>
                    <toggle-button id="checkbox-client-toggles.show_on_marketing_pages"
                                   v-model="client.configuration.toggles.show_on_marketing_pages"
                                   name="checkbox-client-toggles.show_on_marketing_pages"
                                   sync />
                    Show on marketing pages
                  </label>
                </b-col>
                <b-col cols="12">
                  <b-form-group id="group-client-configuration.marketing.co-branded-logo" label="Co-branded Logo URL:"
                                label-for="input-client-configuration.marketing.co-branded-logo">
                    <b-form-input id="input-client-configuration.marketing.co-branded-logo"
                                  v-model.trim="client.configuration.marketing.co_branded_logo"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group label="Incentive Opt-in">
                    <b-form-checkbox-group
                      id="checkbox-client-toogles.incentive_opt_in"
                      v-model="client.configuration.incentives_opt_in"
                      :options="incentivesOptInOptions"
                      stacked />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-tab>

        <b-tab title="Marketing plan" v-if="isMarketingIntegrationEnabled">
          <b-overlay :show="marketingPlansOverlay.show">
            <b-row class="mb-3">
              <b-col cols="12">
                <b-button variant="info" size="sm" class="float-right" v-b-modal.create-marketing-plan-modal>
                  <feather type="plus"/> Create marketing plan
                </b-button>
              </b-col>
            </b-row>
            <b-table :fields="marketing_plans.tableFields" :items="marketing_plans.data"
                     outlined striped empty-text="No marketing plans">
              <template #cell(uuid)="{item}">
                <small>{{ item.uuid }}</small>
              </template>
              <template #cell(actions)="{item}">
                <b-button size="sm" class="mr-4" variant="info" :to="`/client/marketing-plan/edit/${item.uuid}`">
                  <feather type="edit-2" class="mr-2" /> Details
                </b-button>
              </template>
            </b-table>
          </b-overlay>
        </b-tab>

      </b-tabs>
    </b-card-text>

    <b-modal id="create-marketing-plan-modal" title="Create marketing plan" hide-footer>
      <b-button size="sm" class="mr-4" variant="info" :to="`/client/marketing-plan/new?client=${client.id}`">
        <feather type="plus" class="mr-2" /> Create marketing plan for client {{ client.display_name }}
      </b-button>

      <template v-if="aso_groups.data.length > 0">
        <p class="mt-5">Create marketing plan for ASO group</p>
        <multiselect v-model="aso_groups.selected" :options="asoGroupOptions"
                     placeholder="Select ASO group"/>
        <b-button size="sm" class="mt-2" variant="info" v-if="aso_groups.selected"
                  :to="`/client/marketing-plan/new?client=${client.id}&aso=${aso_groups.selected}`">
          <feather type="plus" class="mr-2" />
          Create marketing plan for ASO {{ aso_groups.selected }}
        </b-button>
      </template>
    </b-modal>
  </b-tab>
</template>
